import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Mail, Inbox, Users, Radio, Loader2, CreditCard, Activity, Server, CheckCircle2, XCircle } from 'lucide-react';
import api from '@/lib/adminApi';
import { CountUp } from '@/components/Reveal';

const StatCard = ({ icon: Icon, label, value, prefix = '', sub, accent = '#17FDFD' }) => (
  <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-wider text-white/50">{label}</span>
      <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}1a`, color: accent }}><Icon className="h-4 w-4" /></span>
    </div>
    <div className="mt-3 font-display text-3xl font-bold text-white"><CountUp value={String(value)} prefix={prefix} /></div>
    {sub && <p className="mt-1 text-xs text-white/40">{sub}</p>}
  </div>
);

const INTEGRATIONS = [
  { key: 'revenuecat', name: 'RevenueCat', icon: CreditCard, desc: 'Subscriptions, MRR, tiers & trials', how: 'Add REVENUECAT_API_KEY to the backend to sync real subscription data.' },
  { key: 'metaapi', name: 'MetaAPI', icon: Activity, desc: 'Live MT5 account connection status', how: 'Add METAAPI_TOKEN to the backend to monitor real MT5 connections.' },
  { key: 'app_backend', name: 'App Backend', icon: Server, desc: 'Real app users & signal providers', how: 'Add APP_BACKEND_URL to pull real users and providers from your app.' },
  { key: 'sendgrid', name: 'SendGrid', icon: Mail, desc: 'Contact notifications, auto-replies & newsletter emails', how: 'Add SENDGRID_API_KEY to the backend to send real emails.' },
];

const tooltipStyle = { background: '#0A0A0B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff', fontSize: 12 };

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.get('/admin/kpis').then((r) => setKpis(r.data));
    api.get('/admin/subscribers', { params: { limit: 500 } }).then((r) => setSubs(r.data.subscribers || []));
  }, []);

  if (!kpis) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  // Build real subscriber growth (cumulative by day) from actual signups
  const byDay = {};
  [...subs].reverse().forEach((s) => {
    const d = (s.created_at || '').slice(0, 10);
    if (d) byDay[d] = (byDay[d] || 0) + 1;
  });
  let cum = 0;
  const growth = Object.keys(byDay).sort().map((d) => { cum += byDay[d]; return { day: d.slice(5), total: cum }; });

  const integrations = kpis.integrations || {};
  const rc = kpis.revenuecat?.metrics;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Dashboard</h1>
        <p className="mt-1 text-sm text-white/50">Live data from your Copy.Click marketing site. Connect integrations below to unlock user &amp; revenue analytics.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Mail} label="Newsletter Subscribers" value={kpis.subscribers_total} sub={`${kpis.subscribers_new_week} in the last 7 days`} />
        <StatCard icon={Inbox} label="Contact Messages" value={kpis.contacts_total} sub={`${kpis.contacts_new_week} in the last 7 days`} accent="#00FFA3" />
        <StatCard icon={Users} label="App Users" value={rc ? rc.active_users ?? 0 : kpis.total_users} sub={rc ? 'Active last 28 days (RevenueCat)' : 'Connect a source'} accent="#FFD700" />
        <StatCard icon={Radio} label="Signal Providers" value={kpis.providers_total} sub={integrations.app_backend ? `${kpis.providers_pending} pending` : 'Connect a source'} accent="#FF2E93" />
      </div>

      {rc && (
        <div>
          <div className="mb-4 flex items-center gap-2">
            <h3 className="font-display text-lg font-semibold text-white">Revenue &amp; Subscriptions</h3>
            <span className="rounded-full bg-success/15 px-2.5 py-0.5 text-[11px] font-semibold text-success">RevenueCat · Live</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={CreditCard} label="MRR" prefix="$" value={rc.mrr ?? 0} sub="Monthly recurring revenue" />
            <StatCard icon={CreditCard} label="Active Subscriptions" value={rc.active_subscriptions ?? 0} sub="Paying right now" accent="#00FFA3" />
            <StatCard icon={Activity} label="Active Trials" value={rc.active_trials ?? 0} sub="In trial period" accent="#FFD700" />
            <StatCard icon={Users} label="New Customers" value={rc.new_customers ?? 0} sub="Last 28 days" accent="#FF2E93" />
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-surface/70 p-6">
        <h3 className="font-display text-lg font-semibold text-white">Subscriber Growth</h3>
        <p className="text-xs text-white/40">Cumulative newsletter sign-ups from your site (real data)</p>
        {growth.length > 1 ? (
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growth} margin={{ left: -10, right: 6 }}>
                <defs>
                  <linearGradient id="grw" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#17FDFD" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#17FDFD" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="day" stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Subscribers']} />
                <Area type="monotone" dataKey="total" stroke="#17FDFD" strokeWidth={2.5} fill="url(#grw)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="mt-8 py-8 text-center text-sm text-white/40">Not enough sign-ups yet to chart a trend. As people subscribe from your site, growth will appear here.</p>
        )}
      </div>

      <div>
        <h3 className="mb-1 font-display text-lg font-semibold text-white">Data Integrations</h3>
        <p className="mb-4 text-sm text-white/50">Connect these to replace empty states with real user, subscription and MT5 data.</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {INTEGRATIONS.map((it) => {
            const connected = !!integrations[it.key];
            return (
              <div key={it.key} data-testid={`admin-integration-${it.key}`} className="rounded-2xl border border-white/10 bg-surface/70 p-5">
                <div className="flex items-center justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-white/70"><it.icon className="h-5 w-5" /></span>
                  {connected ? (
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-success"><CheckCircle2 className="h-4 w-4" /> Connected</span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-white/40"><XCircle className="h-4 w-4" /> Not connected</span>
                  )}
                </div>
                <p className="mt-4 font-display text-base font-semibold text-white">{it.name}</p>
                <p className="mt-1 text-xs text-white/50">{it.desc}</p>
                {!connected && <p className="mt-3 rounded-lg bg-white/5 px-3 py-2 text-[11px] leading-relaxed text-white/40">{it.how}</p>}
                {connected && it.key === 'app_backend' && !integrations.app_backend_authed && <p className="mt-3 rounded-lg bg-brand/10 px-3 py-2 text-[11px] leading-relaxed text-brand/80">Plans &amp; MT5 servers are live. Add APP_API_TOKEN to also sync providers &amp; users.</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
